import React, { useRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, useRouter, Stack } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet } from '../../../src/api/client';
import { useToast } from '../../../src/components/Toast';
import Input from '../../../src/components/Input';
import BayEquipmentConfig, { type BayEquipmentConfigHandle } from '../../../src/components/bay/BayEquipmentConfig';
import Button from '../../../src/components/Button';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';

interface Tier {
  tierId?: string;
  name: string;
  price: string;
  enabled: boolean;
}
interface WashTier { id: string; name: string; priceInCents: number; isActive: boolean }

interface PackageRow {
  name: string;
  price: string;    // dollars, converted to cents on save
  minutes: string;  // whole minutes, converted to durationMs on save
}

/** Hardware fallback when the attached controller hasn't reported a heartbeat yet. */
const DEFAULT_RELAY_COUNT = 4;

/** Resize a tier list to exactly `count` rows, preserving overlap. Pads new slots disabled. */
function resizeTiers(existing: Tier[], count: number): Tier[] {
  if (existing.length === count) return existing;
  if (existing.length > count) return existing.slice(0, count);
  const padded = [...existing];
  for (let i = existing.length; i < count; i++) {
    padded.push({ name: `Tier ${i + 1}`, price: '', enabled: false });
  }
  return padded;
}

/** Auto-fill minutes from a package price at the master rate ($/min). */
function minutesFromPrice(priceDollars: string, rateDollars: string): string {
  const price = parseFloat(priceDollars);
  const rate = parseFloat(rateDollars);
  if (!price || !rate || price <= 0 || rate <= 0) return '';
  return String(Math.max(1, Math.round(price / rate)));
}

/** EditBayScreen - modify bay pricing, tiers, relay config, and controller.
 *  Self-serve bays and vacuums share the pricing model: master Rate ($/min)
 *  plus the Metered (count up) / Timed Packages (count down) segmented
 *  buttons — the selected button IS the active family; saving clears the
 *  unselected one. */
export default function EditBayScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { bayId } = useLocalSearchParams<{ bayId: string }>();
  const [name, setName] = useState('');
  const [type, setType] = useState<'self-serve' | 'automatic' | 'vacuum'>('self-serve');
  // ── Pricing family (self-serve + vacuum): the segmented buttons ARE the
  // enable choice — exactly one family is active at a time. ──
  const [pricePerMinute, setPricePerMinute] = useState('');   // master rate $/min
  const [activeSection, setActiveSection] = useState<'metered' | 'packages'>('metered');
  const [minimumCharge, setMinimumCharge] = useState('');
  const [maximumCharge, setMaximumCharge] = useState('');
  const [packages, setPackages] = useState<PackageRow[]>([]);
  const [allowCustomAmount, setAllowCustomAmount] = useState(false);
  // Optional custom-amount bounds in DOLLARS (wave 2, mig 0070). Empty = no bound.
  const [customAmountMin, setCustomAmountMin] = useState('');
  const [customAmountMax, setCustomAmountMax] = useState('');
  /** Unlocks Minutes from the rate math so packages can carry bulk discounts. */
  const [overrideMinutes, setOverrideMinutes] = useState(false);
  // Automatic wash tiers (relay-bound, catalog-driven)
  const [tiers, setTiers] = useState<Tier[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [controllerId, setControllerId] = useState('');
  // Owner confirmed moving an in-use controller here (picker dialog) — send
  // reassign:true so the backend releases the other bay/machine on save.
  const [controllerReassign, setControllerReassign] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [relayCount, setRelayCount] = useState<number>(DEFAULT_RELAY_COUNT);
  const [controllerStatus, setControllerStatus] = useState<'unattached' | 'loading' | 'found' | 'notfound'>('unattached');
  // Relay config (Advanced section). Only sent on save when touched, so an
  // untouched form never overwrites hand-tuned values with rebuilt defaults.
  const [advTouched, setAdvTouched] = useState(false);
  const [coinPulseMs, setCoinPulseMs] = useState('70');
  const [coinGapMs, setCoinGapMs] = useState('70');
  const [coinValueCents, setCoinValueCents] = useState('25');
  const [autoPulses, setAutoPulses] = useState<string[]>([]);
  const [maxSessionMinutes, setMaxSessionMinutes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    const ac = new AbortController();
    const fetchBay = async () => {
      try {
        const bay = await api<any>(`/api/bays/${bayId}`);
        if (ac.signal.aborted) return;
        setName(bay.name);
        setType(bay.type);
        setLocationId(bay.locationId);
        setIsActive(bay.isActive);
        setControllerId(bay.controllerId || '');
        if (bay.type === 'self-serve' || bay.type === 'vacuum') {
          setPricePerMinute(bay.pricePerMinute?.toString() || '');
          // Vacuums default to no minimum (empty = $0); self-serve keeps the
          // classic $3 floor default.
          setMinimumCharge(bay.minimumCharge != null ? (bay.minimumCharge / 100).toFixed(2) : (bay.type === 'vacuum' ? '' : '3.00'));
          setMaximumCharge(bay.maximumCharge != null ? (bay.maximumCharge / 100).toFixed(2) : '');
          setAllowCustomAmount(bay.allowCustomAmount ?? false);
          setCustomAmountMin(bay.customAmountMinCents != null ? (bay.customAmountMinCents / 100).toFixed(2) : '');
          setCustomAmountMax(bay.customAmountMaxCents != null ? (bay.customAmountMaxCents / 100).toFixed(2) : '');
          const pkgRows: PackageRow[] = (bay.tiers ?? []).map((t: any) => ({
            name: t.name,
            price: (t.price / 100).toFixed(2),
            minutes: String(Math.round((t.durationMs || 0) / 60000)),
          }));
          if (pkgRows.length > 0) {
            setPackages(pkgRows);
            // Packages priced off the rate curve = a bulk discount is in play;
            // pre-check the override so editing doesn't snap them back.
            const rateNum = bay.pricePerMinute || 0;
            if (rateNum > 0 && pkgRows.some(r => {
              const price = parseFloat(r.price);
              const mins = parseFloat(r.minutes);
              return price > 0 && mins > 0 && Math.abs(price - mins * rateNum) > 0.011;
            })) {
              setOverrideMinutes(true);
            }
          } else if (Array.isArray(bay.presets) && bay.presets.length > 0) {
            // Legacy presetsJson (no packages yet): prefill package rows from
            // the presets — they land under the Timed Packages selection, so
            // saving with that family selected upgrades the bay to packages.
            const rateNum = bay.pricePerMinute || 0;
            const noun = bay.type === 'vacuum' ? 'Vacuum' : 'Wash';
            setPackages(bay.presets.map((p: number) => ({
              name: `$${(p % 100 === 0 ? String(p / 100) : (p / 100).toFixed(2))} ${noun}`,
              price: (p / 100).toFixed(2),
              minutes: rateNum > 0 ? String(Math.max(1, Math.round(p / 100 / rateNum))) : '',
            })));
          }
          // The selected family comes from the bay's data: packages when the
          // bay has tiers, metered otherwise.
          setActiveSection(pkgRows.length > 0 ? 'packages' : 'metered');
        } else if (bay.tiers) {
          setTiers(bay.tiers.map((t: any) => ({
            tierId: t.tierId,
            name: t.name,
            price: (t.price / 100).toFixed(2),
            enabled: t.enabled !== false,
          })));
        }
        // Load relay config (Advanced prefill)
        if (bay.relayConfig) {
          const rc = bay.relayConfig;
          if (rc.coinPulse) {
            setCoinPulseMs(String(rc.coinPulse.pulseMs || 70));
            setCoinGapMs(String(rc.coinPulse.gapMs || 70));
          }
          if (rc.coinValueCents) setCoinValueCents(String(rc.coinValueCents));
          if (Array.isArray(rc.tiers)) {
            setAutoPulses(rc.tiers.map((t: any) => String(t?.pulseMs || 300)));
          }
        }
        if (bay.maxWashTimeMs) setMaxSessionMinutes(String(Math.round(bay.maxWashTimeMs / 60000)));
      } catch (err: any) {
        if (ac.signal.aborted) return;
        toast.show({ type: 'error', title: 'Error', message: err.message });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    };
    fetchBay();
    return () => ac.abort();
  }, [bayId]);

  // Load the owner's wash-tier catalog (source of truth for tier name + price).
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        const catalog = await apiGet<WashTier[]>('/api/wash-tiers');
        if (!ac.signal.aborted) setWashTiers(catalog.filter(t => t.isActive));
      } catch { /* non-fatal: picker just shows empty */ }
    })();
    return () => ac.abort();
  }, []);

  // Fetch the attached controller's relay count and auto-resize the tier list.
  // Debounced so the owner can type a controller ID without thrashing the API.
  useEffect(() => {
    if (type !== 'automatic') return;
    const trimmed = controllerId.trim();
    if (!trimmed) {
      setControllerStatus('unattached');
      setRelayCount(DEFAULT_RELAY_COUNT);
      setTiers(prev => resizeTiers(prev, DEFAULT_RELAY_COUNT));
      return;
    }
    setControllerStatus('loading');
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const ctrl = await api<{ relayCount: number }>(`/api/controllers/${trimmed}`);
        if (ac.signal.aborted) return;
        const n = ctrl.relayCount || DEFAULT_RELAY_COUNT;
        setRelayCount(n);
        setControllerStatus('found');
        setTiers(prev => resizeTiers(prev, n));
      } catch {
        if (ac.signal.aborted) return;
        setControllerStatus('notfound');
        setRelayCount(DEFAULT_RELAY_COUNT);
        setTiers(prev => resizeTiers(prev, DEFAULT_RELAY_COUNT));
      }
    }, 350);
    return () => { ac.abort(); clearTimeout(timer); };
  }, [controllerId, type]);

  const updateTier = (i: number, field: keyof Tier, value: string | boolean) => {
    const updated = [...tiers];
    updated[i] = { ...updated[i], [field]: value as never };
    setTiers(updated);
  };

  // Assign a catalog tier to relay slot i; denormalize its name + price.
  // Empty string clears the selection (used by the placeholder option).
  const selectTier = (i: number, tierId: string) => {
    const updated = [...tiers];
    if (!tierId) {
      updated[i] = { ...updated[i], tierId: undefined };
    } else {
      const t = washTiers.find(x => x.id === tierId);
      if (!t) return;
      updated[i] = { ...updated[i], tierId: t.id, name: t.name, price: (t.priceInCents / 100).toFixed(2) };
    }
    setTiers(updated);
  };

  const dualFamily = type === 'self-serve' || type === 'vacuum';
  const noun = type === 'vacuum' ? 'vacuum' : 'wash';

  const updatePackage = (i: number, field: keyof PackageRow, v: string) => {
    setPackages(prev => {
      const u = [...prev];
      u[i] = { ...u[i], [field]: v };
      // Bidirectional rate math: the last-edited field drives the other via
      // the master rate (5 min → price fills in; $5 → minutes fill in). With
      // the bulk-discount override on, minutes are unlocked from the rate
      // (price still fills an EMPTY minutes as a hint).
      const rateNum = parseFloat(pricePerMinute);
      if (field === 'price' && (!overrideMinutes || !u[i].minutes)) {
        const auto = minutesFromPrice(v, pricePerMinute);
        if (auto) u[i] = { ...u[i], minutes: auto };
      } else if (field === 'minutes' && !overrideMinutes) {
        const mins = parseFloat(v);
        if (mins > 0 && rateNum > 0) u[i] = { ...u[i], price: (mins * rateNum).toFixed(2) };
      }
      return u;
    });
  };

  /** Validate + build the tiers payload from the package rows. Returns null
   *  after toasting on error; [] when no usable rows. */
  const buildPackages = (): { name: string; price: number; durationMs: number; enabled: boolean }[] | null => {
    const rows = packages.filter(r => r.name.trim() || r.price || r.minutes);
    const parsed = rows.map(r => ({
      name: r.name.trim(),
      price: Math.round(parseFloat(r.price) * 100),
      durationMs: Math.round(parseFloat(r.minutes) * 60000),
      enabled: true,
    }));
    if (parsed.some(t => !t.name)) { toast.show({ type: 'error', title: 'Each package needs a name' }); return null; }
    if (parsed.some(t => isNaN(t.price) || t.price <= 0)) { toast.show({ type: 'error', title: 'Each package needs a valid price' }); return null; }
    if (parsed.some(t => isNaN(t.durationMs) || t.durationMs < 60000 || t.durationMs > 3600000)) {
      toast.show({ type: 'error', title: 'Package minutes must be between 1 and 60' });
      return null;
    }
    return parsed;
  };

  const equipRef = useRef<BayEquipmentConfigHandle>(null);
  const handleSave = async () => {
    if (!name) { toast.show({ type: 'error', title: 'Bay name is required' }); return; }

    const body: any = { name, isActive };

    if (dualFamily) {
      // The selected button IS the enable choice: exactly one family active,
      // the other is cleared on save.
      const rateNum = parseFloat(pricePerMinute);
      // The master rate drives metered sessions AND package/custom-amount
      // conversion; self-serve AND vacuums always require it (backend
      // contract — vacuums need it in packages mode too, for bundle
      // valuation and grant sessions).
      if (type === 'self-serve' || type === 'vacuum' || activeSection === 'metered' || allowCustomAmount) {
        if (!rateNum || rateNum <= 0) {
          toast.show({ type: 'error', title: type === 'vacuum' ? 'Rate ($/min) is required for vacuums' : 'Rate ($/min) is required' });
          return;
        }
      }
      if (rateNum > 0) body.pricePerMinute = rateNum;

      if (activeSection === 'metered') {
        body.minimumCharge = Math.round(parseFloat(minimumCharge || (type === 'vacuum' ? '0' : '3')) * 100);
        const maxCharge = parseFloat(maximumCharge);
        if (!maxCharge || maxCharge <= 0) { toast.show({ type: 'error', title: 'Maximum charge is required for metered pricing' }); return; }
        body.maximumCharge = Math.round(maxCharge * 100);
        if (body.maximumCharge <= body.minimumCharge) {
          toast.show({ type: 'error', title: 'Maximum charge must be greater than minimum charge' });
          return;
        }
        // Count up selected — count-down packages are cleared, and so are
        // legacy preset amounts (they render as a second family on the pay
        // page and prefill the Timed Packages rows here).
        body.tiers = null;
        body.presets = null;
      } else {
        const pkgs = buildPackages();
        if (!pkgs) return;
        if (pkgs.length === 0) { toast.show({ type: 'error', title: 'Add at least one timed package' }); return; }
        body.tiers = pkgs;
        // Packages supersede legacy presets — saving upgrades the bay.
        body.presets = null;
        // Count down selected — clearing the max charge disables count-up.
        body.maximumCharge = null;
        // Vacuums keep their Minimum Charge in packages mode too — it's the
        // per-session floor for bundle and membership vacuum sessions.
        if (type === 'vacuum') {
          body.minimumCharge = Math.round(parseFloat(minimumCharge || '0') * 100) || 0;
        }
      }
      body.allowCustomAmount = allowCustomAmount;
      // Custom-amount bounds (dollars → cents; empty = no bound = null).
      if (allowCustomAmount) {
        const minNum = customAmountMin ? parseFloat(customAmountMin) : null;
        const maxNum = customAmountMax ? parseFloat(customAmountMax) : null;
        if (minNum != null && (isNaN(minNum) || minNum < 0)) {
          toast.show({ type: 'error', title: 'Custom amount minimum must be $0 or more' });
          return;
        }
        if (maxNum != null && (isNaN(maxNum) || maxNum < 0)) {
          toast.show({ type: 'error', title: 'Custom amount maximum must be $0 or more' });
          return;
        }
        if (minNum != null && maxNum != null && maxNum < minNum) {
          toast.show({ type: 'error', title: 'Custom amount maximum must be at least the minimum' });
          return;
        }
        body.customAmountMinCents = minNum != null ? Math.round(minNum * 100) : null;
        body.customAmountMaxCents = maxNum != null ? Math.round(maxNum * 100) : null;
      } else {
        body.customAmountMinCents = null;
        body.customAmountMaxCents = null;
      }
    }

    // Max session time (metered bays) — only sent when touched.
    if (advTouched && dualFamily && maxSessionMinutes) {
      const mins = parseInt(maxSessionMinutes);
      if (isNaN(mins) || mins < 1 || mins > 60) {
        toast.show({ type: 'error', title: 'Max session time must be 1–60 minutes' });
        return;
      }
      body.maxWashTimeMs = mins * 60000;
    }

    setSaving(true);
    try {
      await api(`/api/bays/${bayId}`, { method: 'PUT', body });
      // Round 2: one Save button saves pricing AND the Controller Setup below; stay on the page when that part fails.
      if (equipRef.current) { const ok = await equipRef.current.save(); if (!ok) { setSaving(false); return; } }

      toast.show({ type: 'success', title: 'Equipment updated' });
      router.replace(`/owner/location/${locationId}` as never);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <View style={styles.container}><Text style={styles.loadingText}>Loading...</Text></View>;

  return (
    <>
      <Stack.Screen options={{ title: 'Edit Equipment' }} />
      <KeyboardAvoidingView style={styles.container} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
          <ContentContainer>
          <Input label="Name" value={name} onChangeText={setName} icon="construct-outline" maxLength={32} />

          <Text style={styles.fieldLabel}>Equipment Type</Text>
          <View style={styles.typeDisplay}>
            <Ionicons name={type === 'automatic' ? 'car' : type === 'vacuum' ? 'sparkles' : 'timer'} size={20} color={colors.primary} />
            <Text style={styles.typeDisplayText}>
              {type === 'automatic' ? 'Automatic' : type === 'vacuum' ? 'Vacuum' : 'Self-Serve'}
            </Text>
            <Text style={styles.typeNote}>(type cannot be changed)</Text>
          </View>

          <View style={styles.activeRow}>
            <Text style={styles.fieldLabel}>Active</Text>
            <TouchableOpacity onPress={() => setIsActive(!isActive)} style={[styles.toggle, isActive && styles.toggleActive]}>
              <Text style={[styles.toggleText, isActive && styles.toggleTextActive]}>{isActive ? 'Yes' : 'No'}</Text>
            </TouchableOpacity>
          </View>

          {dualFamily ? (
            <View>
              {/* Master rate — drives metered sessions, package minute
                  auto-fill, and custom-amount conversion. */}
              <Input
                label="This unit's rate ($/min)"
                placeholder="0.50"
                value={pricePerMinute}
                onChangeText={(v: string) => setPricePerMinute(v.replace(/[^0-9.]/g, ''))}
                keyboardType="decimal-pad"
                icon="cash-outline"
              />
              <Text style={styles.helpText}>
                This bay/vacuum's own per-minute rate — every unit is priced independently. Package prices auto-fill their minutes from it; custom amounts convert at this rate.
              </Text>

              {/* Segmented control — the selected button IS the active family
                  (one or the other; saving clears the unselected one). */}
              <View style={styles.segmentRow}>
                <TouchableOpacity
                  style={[styles.segmentBtn, activeSection === 'metered' && styles.segmentActive]}
                  onPress={() => setActiveSection('metered')}
                >
                  <Text style={[styles.segmentTitle, activeSection === 'metered' && styles.segmentTitleActive]}>
                    Metered
                  </Text>
                  <Text style={styles.segmentDesc}>Count up — pay for time used with min and max values</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.segmentBtn, activeSection === 'packages' && styles.segmentActive]}
                  onPress={() => {
                    setActiveSection('packages');
                    if (packages.length === 0) setPackages([{ name: '', price: '', minutes: '' }]);
                  }}
                >
                  <Text style={[styles.segmentTitle, activeSection === 'packages' && styles.segmentTitleActive]}>
                    Timed Packages
                  </Text>
                  <Text style={styles.segmentDesc}>Count down — prepaid packages and custom amounts</Text>
                </TouchableOpacity>
              </View>

              {activeSection === 'metered' ? (
                <View style={styles.sectionBody}>
                  <Input
                    label="Minimum Charge ($)"
                    placeholder="3.00"
                    value={minimumCharge}
                    onChangeText={(v: string) => setMinimumCharge(v.replace(/[^0-9.]/g, ''))}
                    keyboardType="decimal-pad"
                    icon="pricetag-outline"
                  />
                  {type === 'vacuum' && (
                    <Text style={styles.helpText}>
                      Minimum per session — also applies to bundle and membership vacuum sessions
                    </Text>
                  )}
                  <Input
                    label="Maximum Charge ($)"
                    placeholder="Required"
                    value={maximumCharge}
                    onChangeText={(v: string) => setMaximumCharge(v.replace(/[^0-9.]/g, ''))}
                    keyboardType="decimal-pad"
                    icon="trending-up-outline"
                  />
                  <Text style={styles.helpText}>
                    Customers start a live session and pay for the time they use at the rate above — at least the minimum; the {noun} auto-stops at the maximum.
                  </Text>
                </View>
              ) : (
                <View style={styles.sectionBody}>
                  {/* Vacuum Minimum Charge stays visible in packages mode —
                      bundle/membership sessions run metered under the hood
                      and this is their per-session floor. */}
                  {type === 'vacuum' && (
                    <>
                      <Input
                        label="Minimum Charge ($)"
                        placeholder="0.00"
                        value={minimumCharge}
                        onChangeText={(v: string) => setMinimumCharge(v.replace(/[^0-9.]/g, ''))}
                        keyboardType="decimal-pad"
                        icon="pricetag-outline"
                      />
                      <Text style={styles.helpText}>
                        Minimum per session — also applies to bundle and membership vacuum sessions
                      </Text>
                    </>
                  )}
                  <View style={styles.pkgHeaderRow}>
                    <Text style={[styles.pkgHeader, styles.pkgColName]}>Name</Text>
                    <Text style={[styles.pkgHeader, styles.pkgColSmall]}>Price</Text>
                    <Text style={[styles.pkgHeader, styles.pkgColSmall]}>Minutes</Text>
                    {packages.length > 1 && <View style={styles.pkgColRemove} />}
                  </View>
                  {packages.map((p, i) => (
                    <View key={i} style={styles.pkgRow}>
                      <View style={styles.pkgColName}>
                        <Input placeholder="Quick Wash" value={p.name} onChangeText={(v: string) => updatePackage(i, 'name', v)} maxLength={32} />
                      </View>
                      <View style={styles.pkgColSmall}>
                        <Input placeholder="$" value={p.price} onChangeText={(v: string) => updatePackage(i, 'price', v.replace(/[^0-9.]/g, ''))} keyboardType="decimal-pad" />
                      </View>
                      <View style={styles.pkgColSmall}>
                        <Input placeholder="Min" value={p.minutes} onChangeText={(v: string) => updatePackage(i, 'minutes', v.replace(/[^0-9]/g, ''))} keyboardType="numeric" />
                      </View>
                      {packages.length > 1 && (
                        <TouchableOpacity onPress={() => setPackages(packages.filter((_, idx) => idx !== i))} style={styles.pkgColRemove}>
                          <Ionicons name="close-circle" size={24} color={colors.danger} />
                        </TouchableOpacity>
                      )}
                    </View>
                  ))}
                  {packages.length < 6 && (
                    <TouchableOpacity onPress={() => setPackages([...packages, { name: '', price: '', minutes: '' }])} style={styles.addPresetBtn}>
                      <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
                      <Text style={styles.addTierText}>Add Package</Text>
                    </TouchableOpacity>
                  )}
                  <Text style={styles.helpText}>
                    {overrideMinutes
                      ? 'Minutes are unlocked — set any minutes for a price (bulk discounts).'
                      : 'Minutes and price fill each other in from the rate above — type either one.'}
                  </Text>
                  <TouchableOpacity style={styles.checkRow} onPress={() => setOverrideMinutes(!overrideMinutes)}>
                    <Ionicons name={overrideMinutes ? 'checkbox' : 'square-outline'} size={24} color={overrideMinutes ? colors.primary : colors.textLight} />
                    <Text style={styles.checkLabel}>Override minutes (bulk discounts)</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={styles.checkRow} onPress={() => setAllowCustomAmount(!allowCustomAmount)}>
                    <Ionicons name={allowCustomAmount ? 'checkbox' : 'square-outline'} size={24} color={allowCustomAmount ? colors.primary : colors.textLight} />
                    <Text style={styles.checkLabel}>Allow custom amounts</Text>
                  </TouchableOpacity>
                  <Text style={styles.helpText}>Custom amounts convert to time at the master rate.</Text>
                  {allowCustomAmount && (
                    <>
                      <View style={styles.relayRow}>
                        <View style={{ flex: 1 }}>
                          <Input
                            label="Min $ (optional)"
                            placeholder="No minimum"
                            value={customAmountMin}
                            onChangeText={(v: string) => setCustomAmountMin(v.replace(/[^0-9.]/g, ''))}
                            keyboardType="decimal-pad"
                            icon="arrow-down-outline"
                          />
                        </View>
                        <View style={{ flex: 1 }}>
                          <Input
                            label="Max $ (optional)"
                            placeholder="No maximum"
                            value={customAmountMax}
                            onChangeText={(v: string) => setCustomAmountMax(v.replace(/[^0-9.]/g, ''))}
                            keyboardType="decimal-pad"
                            icon="arrow-up-outline"
                          />
                        </View>
                      </View>
                      <Text style={styles.helpText}>
                        {(() => {
                          const rateNum = parseFloat(pricePerMinute);
                          const minNum = parseFloat(customAmountMin);
                          const maxNum = parseFloat(customAmountMax);
                          if (!rateNum || rateNum <= 0) return 'Bounds for the amount a customer may type. Set the rate above to see the minutes they buy.';
                          const minPart = minNum > 0 ? `$${minNum.toFixed(2)} ≈ ${Math.max(1, Math.round(minNum / rateNum))} min` : null;
                          const maxPart = maxNum > 0 ? `$${maxNum.toFixed(2)} ≈ ${Math.max(1, Math.round(maxNum / rateNum))} min` : null;
                          if (minPart && maxPart) return `At the rate above: ${minPart} · ${maxPart}.`;
                          if (minPart) return `At the rate above: ${minPart}. No maximum set.`;
                          if (maxPart) return `At the rate above: ${maxPart}. No minimum set.`;
                          return 'No bounds set — customers may enter any amount.';
                        })()}
                      </Text>
                    </>
                  )}
                </View>
              )}
            </View>
          ) : null}

          {/* Max session time for metered bays (was in the old Advanced card). */}
          {dualFamily ? (
            <Input
              label="Max Session Time (minutes)"
              placeholder="Auto from max charge"
              value={maxSessionMinutes}
              onChangeText={(v: string) => { setAdvTouched(true); setMaxSessionMinutes(v.replace(/[^0-9]/g, '')); }}
              keyboardType="numeric"
              icon="hourglass-outline"
            />
          ) : null}

          {/* 1.4 P17 / round 2: controllers, relay mapping, inputs and behavior — saved by the single Save button below. */}
          <View style={styles.controllerSection}>
            <Text style={styles.sectionTitle}>Controller Setup</Text>
            <BayEquipmentConfig ref={equipRef} bayId={bayId} bayType={type} hideSave />
          </View>

          <Button title="Save Changes" onPress={handleSave} loading={saving} size="lg" style={styles.saveBtn} />
          <Button title="Cancel" onPress={() => router.back()} variant="outline" size="md" style={styles.cancelBtn} />
          </ContentContainer>
        </ScrollView>
      </KeyboardAvoidingView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  scroll: { padding: spacing.lg },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xxl },
  fieldLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.sm },
  typeDisplay: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.lg, padding: spacing.md, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.md },
  typeDisplayText: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text },
  typeNote: { fontSize: fontSize.xs, color: c.textLight },
  activeRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.lg },
  toggle: { paddingHorizontal: spacing.lg, paddingVertical: spacing.sm, borderRadius: borderRadius.md, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  toggleActive: { backgroundColor: c.primary + '15', borderColor: c.primary },
  toggleText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.textSecondary },
  toggleTextActive: { color: c.primary },
  // Segmented pricing-family control
  segmentRow: { flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.md },
  segmentBtn: {
    flex: 1, padding: spacing.md, borderRadius: borderRadius.md,
    borderWidth: 2, borderColor: c.border, gap: spacing.xs,
  },
  segmentActive: { borderColor: c.primary, backgroundColor: c.primaryBg },
  segmentTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.textSecondary },
  segmentTitleActive: { color: c.primary },
  segmentDesc: { fontSize: fontSize.xs, color: c.textLight },
  sectionBody: { marginBottom: spacing.sm },
  // Package row editor
  pkgHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs },
  pkgHeader: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary, textTransform: 'uppercase' as const },
  pkgRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  pkgColName: { flex: 2 },
  pkgColSmall: { flex: 1 },
  pkgColRemove: { width: 28, paddingBottom: spacing.md, alignItems: 'center' as const },
  addTierText: { fontSize: fontSize.sm, color: c.primaryDark, fontWeight: fontWeight.semibold },
  removeTier: { paddingBottom: spacing.md },
  tierBlock: {
    borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.md,
    padding: spacing.sm, marginBottom: spacing.sm, backgroundColor: c.surface,
  },
  tierBlockHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: spacing.xs,
  },
  tierRelayLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary },
  enabledToggle: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: spacing.sm, paddingVertical: 4,
    borderRadius: borderRadius.sm, borderWidth: 1, borderColor: c.border,
    backgroundColor: c.surfaceSecondary,
  },
  enabledToggleOn: { backgroundColor: c.primary, borderColor: c.primary },
  enabledToggleText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.textSecondary },
  enabledToggleTextOn: { color: '#fff' },
  helpText: { fontSize: fontSize.xs, color: c.textLight, marginTop: -spacing.sm, marginBottom: spacing.md },
  // Hint shown directly under the per-relay tier Picker. Uses a positive top
  // margin so it sits below the dropdown instead of overlapping it (helpText's
  // negative marginTop is tuned to snug under section labels, not a Picker).
  tierHint: { fontSize: fontSize.xs, color: c.textLight, marginTop: spacing.xs },
  controllerSection: { marginTop: spacing.lg, paddingTop: spacing.lg, borderTopWidth: 1, borderTopColor: c.border },
  sectionTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.xs },
  saveBtn: { marginTop: spacing.lg },
  cancelBtn: { marginTop: spacing.sm, marginBottom: spacing.xxl },
  presetsWrap: { marginBottom: spacing.sm },
  presetRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  addPresetBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingVertical: spacing.sm },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, marginBottom: spacing.xs },
  checkLabel: { fontSize: fontSize.sm, color: c.text },
  relayRow: { flexDirection: 'row', gap: spacing.sm },
  advancedCard: { marginTop: spacing.lg },
  autoPulseRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, marginBottom: spacing.xs },
  autoPulseLabel: { flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary },
  autoPulseInput: { flex: 1 },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
